import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import en from './locales/en';
import ko from './locales/ko';
import bn from './locales/bn';
import { supabase } from '@/lib/supabaseClient';

const messages = {
  en: { translation: en },
  ko: { translation: ko },
  bn: { translation: bn },
};

i18n
  .use(initReactI18next)
  .init({
    lng: 'en',
    fallbackLng: 'en',
    debug: false,
    resources: messages,
    interpolation: {
      escapeValue: false,
    },
  });

// Admin-editable site text: rxk_site_content overrides the bundled defaults
// above at runtime, per locale. Fetched once per language per page load —
// if the table is empty, unreachable, or missing a key, the bundled default
// keeps rendering (this must never be able to take the site down).
const overriddenLangs = new Set<string>();

async function applyContentOverrides(lng: string) {
  if (overriddenLangs.has(lng)) return;
  overriddenLangs.add(lng);
  try {
    const { data, error } = await supabase.from('rxk_site_content').select('key, value').eq('locale', lng);
    if (error || !data || data.length === 0) return;
    // addResourceBundle needs a pre-nested object to resolve dotted keys
    // (e.g. 'footer.hotline') — handing it a flat { 'footer.hotline': v }
    // object adds a literal "footer.hotline" top-level key instead, which
    // t('footer.hotline')'s keySeparator lookup never matches, so every
    // dotted override silently fell back to the bundled default no matter
    // what was saved. addResource resolves the keySeparator itself per key.
    for (const row of data as { key: string; value: string }[]) {
      i18n.addResource(lng, 'translation', row.key, row.value);
    }
    // addResource updates the store silently — react-i18next's useTranslation
    // only re-renders on 'languageChanged'/'loaded', neither of which
    // addResource fires, so every already-mounted component kept showing
    // the bundled default until something else (a nav, a lang switch)
    // happened to force it. Emitting this after the override load is done
    // makes every t('footer.hotline')-style call re-resolve immediately,
    // this page load, without waiting for an unrelated re-render trigger.
    i18n.emit('languageChanged', lng);
  } catch {
    // Network/DB issue — keep the bundled defaults, don't retry this session.
  }
}

i18n.on('languageChanged', (lng) => {
  void applyContentOverrides(lng);
});
void applyContentOverrides(i18n.language);

export default i18n;